import { useState } from "react"
import { Box, TextField, IconButton, Typography, CircularProgress, ArrowUpwardIcon } from "src/components"
import { useIsMobile, useSnackbar } from 'src/hooks';

export default function MessageInput({ isSendingMessage, onSend, footerHeight, disableInput = false }) {
  const [text, setText] = useState("");

  const isMobile = useIsMobile();
  const { openSnackbar } = useSnackbar();

  const handleSendMessage = async () => {
    if (!text.trim() || isSendingMessage) return

    try {
      onSend(text);   
    } catch (error) {
      console.error("Failed to send message:", error);
      openSnackbar("Failed to send message. Please try again", "error");
    } finally {
      setText("");
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  }

  return (
    <Box
      sx={{
        marginBottom: `${footerHeight}px`,
        bgcolor: "#fff",
        borderTop: "1px solid #e0e0e0",
        pt: 2,
        pb: 1,
        px: isMobile ? 3 : 20,
        zIndex: 10,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          bgcolor: "#f8f9fa",
          borderRadius: "24px",
          border: "1px solid #e1e5e9",
          px: 2,
          "&:focus-within": {
            borderColor: "secondary.main",
            boxShadow: "0 0 0 2px rgba(25, 118, 210, 0.1)",
          },
        }}
      >
        <TextField
          variant="standard"
          placeholder="Type a message..."
          fullWidth
          multiline
          maxRows={4}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isSendingMessage}
          InputProps={{
            disableUnderline: true,
            sx: {
              fontSize: "14px",
              lineHeight: 1.4,
              "& .MuiInputBase-input": {
                py: 1,
                "&::-webkit-scrollbar": {
                  display: "none",
                }
              },
            },
          }}
        />

        <IconButton
          onClick={handleSendMessage}
          disabled={!text.trim() || isSendingMessage || disableInput}
          sx={{
            ml: 1,
            bgcolor: text.trim() && !isSendingMessage ? "secondary.main" : "#e0e0e0",
            color: text.trim() && !isSendingMessage ? "#fff" : "#9e9e9e",
            width: 32,
            height: 32,
            "&:hover": {
              bgcolor: text.trim() && !isSendingMessage ? "secondary.main" : "#e0e0e0",
            },
            "&:disabled": {
              bgcolor: "#e0e0e0",
              color: "#9e9e9e",
            },
          }}
        >
          {isSendingMessage ? (
            <CircularProgress size={16} sx={{ color: "#9e9e9e" }} />
          ) : (
            <ArrowUpwardIcon sx={{ fontSize: 16 }} />
          )}
        </IconButton>
      </Box>

      <Typography
        variant="caption"
        color="textSecondary"
        sx={{
          mt: 1,
          display: "block",
          textAlign: "center",
          fontSize: "12px",
        }}
      >
        Press Enter to send, Shift + Enter for new line
      </Typography>
    </Box>
  )
}
